import { Fragment } from 'react';
import styled, { css } from 'styled-components';

import { Icon } from '@creditornot/alchemy-react';
import * as icons from '@creditornot/alchemy-react/icons';
import { typographyCss } from '@creditornot/cb-ingredients';
import { color } from '@creditornot/cb-ingredients/design-tokens';

const Root = styled.div`
  display: flex;
  align-items: flex-start;
`;

const StepContainer = 'div';

const Circle = styled.button<{ $selected: boolean; $done: boolean; $pending: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 8px 8px;
  ${typographyCss.headline}
  ${({ $selected, $done, $pending }) =>
    (($selected || $done) &&
      css`
        background: ${$selected ? color.bgFillBrand : color.bgFillPositive};
        color: ${color.bg};
        border: 0;
      `) ||
    ($pending &&
      css`
        background: ${color.bgSurface};
        color: ${color.textSubdued};
        border: 0;
      `)}
  &:not(:disabled) {
    cursor: pointer;
  }
`;

const Name = styled.div<{ $selected: boolean; $done: boolean; $pending: boolean }>`
  width: 104px;
  margin: 0 -24px;
  ${typographyCss.body}
  text-align: center;
  ${({ $selected, $done, $pending }) =>
    (($selected || $done) &&
      css`
        color: ${color.text};
        font-weight: ${$selected && '700'};
      `) ||
    ($pending &&
      css`
        color: ${color.textSubdued};
      `)}
`;

const Line = styled.div`
  margin-top: 19px;
  flex-shrink: 0;
  width: 96px;
  border-bottom: 2px dashed ${color.border};
`;

type Step = {
  name: string;
  onClick?: () => void;
};

type Props = {
  steps: Step[];
  current: number;
  className?: string;
};

export const WizardProgress: React.FC<Props> = ({ steps, current, className }) => (
  <Root className={className} data-test-id="wizard-progress">
    {steps.map((step, index) => {
      const number = index + 1;
      const done = number < current;
      const selected = number === current;
      const pending = number > current;
      const last = index === steps.length - 1;
      return (
        <Fragment key={index}>
          <StepContainer>
            <Circle
              $selected={selected}
              $done={done}
              $pending={pending}
              onClick={step.onClick}
              disabled={!done}
              aria-label={step.name}
            >
              {done ? (
                <Icon source={icons.checkmark} size="medium" width={20} height={20} />
              ) : (
                number
              )}
            </Circle>
            <Name $selected={selected} $done={done} $pending={pending}>
              {step.name}
            </Name>
          </StepContainer>
          {!last && (done ? <Line style={{ background: color.bgFillPositive }} /> : <Line />)}
        </Fragment>
      );
    })}
  </Root>
);
